import moment from 'moment/moment';
import { useMemo } from 'react';
import useSWR from 'swr';

import { useSelectedEmployeeId } from '~/hooks/employee';

import { Performance, getEmpPerformance } from './data';

export type UsePerformanceComponentOutput = {
  performanceData: Performance[];
  chart: {
    chartWidth: string;
    chartData: { x: string; y: number }[];
    maxVal: number;
    minVal: number;
  };
  isLoading: boolean;
};

const useGetEmpPerformance = (employeeId: number) => {
  const { data, ...rest } = useSWR(['empPerformance', employeeId], () =>
    getEmpPerformance(employeeId),
  );

  return {
    data: data ?? [],
    ...rest,
  };
};

export const usePerformanceComponent = (): UsePerformanceComponentOutput => {
  const { selectedEmployeeId } = useSelectedEmployeeId();

  const { data: performanceList, isLoading } =
    useGetEmpPerformance(selectedEmployeeId);

  const processedData = useMemo(() => {
    const sortFunction = (a: Performance, b: Performance) => {
      if (a.fTransactionEnteredTime < b.fTransactionEnteredTime) {
        return -1;
      }
      if (a.fTransactionEnteredTime > b.fTransactionEnteredTime) {
        return 1;
      }
      return 0;
    };

    const sortedPerfData = performanceList.sort((a, b) => sortFunction(a, b));
    const chartWidth =
      performanceList.length && performanceList.length > 10
        ? '1120px'
        : (performanceList.length * 100 + 50).toString();

    const chartData = sortedPerfData.map((v) => ({
      x: moment(v.fTransactionEnteredTime).format('YYYY-MM-DD'),
      y: v.fPerfTotalScore,
    }));
    const yValues = chartData.map((obj) => obj.y);
    const maxVal = Math.max(...yValues) + 0.05;
    const minVal = Math.min(...yValues) - 0.05;

    return {
      performanceData: sortedPerfData.reverse(),
      chart: {
        chartWidth,
        chartData,
        maxVal,
        minVal,
      },
    };
  }, [performanceList]);

  return {
    ...processedData,
    isLoading,
  };
};
